/**
 * components/dashboard/TopBar.tsx
 * Market clock, account balance, WebSocket status, logout button.
 */

import { useEffect, useState } from 'react';
import { Wifi, WifiOff, Activity, LogOut, Radar, BookOpen, Bot } from 'lucide-react';
import { useWs, useAccount, useUi } from '../../store';
import type { MainTab } from '../../store';
import clsx from 'clsx';

type MarketSession = 'pre-market' | 'open' | 'after-hours' | 'closed';

function getMarketSession(h: number, m: number): MarketSession {
  if (h >= 9 && (h > 9 || m >= 30) && h < 16) return 'open';
  if ((h >= 4 && h < 9) || (h === 9 && m < 30)) return 'pre-market';
  if (h >= 16 && h < 20) return 'after-hours';
  return 'closed';
}

interface TopBarProps {
  onLogout: () => void;
}

export function TopBar({ onLogout }: TopBarProps) {
  const [time, setTime]       = useState('');
  const [session, setSession] = useState<MarketSession>('closed');
  const { status }            = useWs();
  const { stats }             = useAccount();
  const { mainTab, setMainTab } = useUi();

  useEffect(() => {
    const tick = () => {
      const et = new Date(new Date().toLocaleString('en-US', { timeZone: 'America/New_York' }));
      const h  = et.getHours();
      const m  = et.getMinutes();
      const s  = et.getSeconds();
      const hh = String(h % 12 || 12).padStart(2, '0');
      const mm = String(m).padStart(2, '0');
      const ss = String(s).padStart(2, '0');
      setTime(`${hh}:${mm}:${ss} ${h >= 12 ? 'PM' : 'AM'} ET`);
      setSession(getMarketSession(h, m));
    };
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, []);

  const sessionConfig = {
    'pre-market':  { label: 'PRE-MARKET',  color: 'text-amber', dot: 'bg-amber' },
    'open':        { label: 'MARKET OPEN', color: 'text-bull',  dot: 'bg-bull'  },
    'after-hours': { label: 'AFTER HOURS', color: 'text-sky',   dot: 'bg-sky'   },
    'closed':      { label: 'CLOSED',      color: 'text-ink-3', dot: 'bg-ink-3' },
  };
  const sc = sessionConfig[session];

  const balance  = stats?.balance    ?? 2000;
  const totalPnl = stats?.totalPnl   ?? 0;
  const todayPnl = stats?.todayPnl   ?? 0;
  const maxRisk  = stats?.maxRiskPerTrade ?? 40;

  function handleLogout() {
    if (window.confirm('Sign out of Phase 1 Trader?')) {
      onLogout();
    }
  }

  return (
    <header className="bg-bg-1 border-b border-border flex flex-wrap items-center gap-x-3 gap-y-1.5 px-3 sm:px-4 py-1.5 lg:py-0 lg:h-12 justify-between flex-shrink-0">

      {/* Left: Logo + market status */}
      <div className="flex items-center gap-3 sm:gap-4">
        <div className="font-display font-extrabold text-base tracking-tight">
          PHASE<span className="text-bull">1</span>
        </div>

        <div className="flex items-center gap-1.5">
          <span className={clsx('w-1.5 h-1.5 rounded-full animate-pulse', sc.dot)} />
          <span className={clsx('text-2xs font-mono font-medium', sc.color)}>
            {sc.label}
          </span>
        </div>

        <span className="hidden sm:inline text-xs font-mono text-ink-3">{time}</span>
      </div>

      {/* Center: main tab switcher — its own full-width row below lg */}
      <nav className="order-last basis-full lg:order-none lg:basis-auto flex items-center gap-1">
        {([
          { id: 'scanner',    label: 'Scanner',            Icon: Radar    },
          { id: 'journal',    label: 'Journal & Analytics', Icon: BookOpen },
          { id: 'autotrader', label: 'Auto-Trader',         Icon: Bot      },
        ] as const satisfies { id: MainTab; label: string; Icon: typeof Radar }[]).map(({ id, label, Icon }) => (
          <button
            key={id}
            onClick={() => setMainTab(id)}
            className={clsx(
              'flex-1 lg:flex-none flex items-center justify-center gap-1.5 text-2xs font-mono px-2.5 py-2 lg:py-1 rounded border transition-colors whitespace-nowrap',
              mainTab === id
                ? 'bg-bull/10 border-bull/30 text-bull'
                : 'border-transparent text-ink-3 hover:text-ink-1 hover:bg-bg-2'
            )}
          >
            <Icon size={12} />
            {id === 'journal'
              ? <><span className="sm:hidden">Journal</span><span className="hidden sm:inline">{label}</span></>
              : label}
          </button>
        ))}
      </nav>

      {/* Right: Account info + WS status + logout */}
      <div className="flex items-center gap-2 sm:gap-3">

        {/* P&L strip */}
        <div className="flex items-center gap-2 sm:gap-3 bg-bg-2 border border-border rounded-md px-2 sm:px-3 py-1 text-2xs font-mono">
          <span className="hidden sm:inline text-ink-3">Balance</span>
          <span className="text-ink-1 font-medium">
            ${balance.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
          </span>
          <span className="text-ink-3">·</span>
          <span className="text-ink-3">Today</span>
          <span className={todayPnl >= 0 ? 'text-bull' : 'text-bear'}>
            {todayPnl >= 0 ? '+' : ''}${todayPnl.toFixed(2)}
          </span>
          <span className="hidden md:flex items-center gap-3">
            <span className="text-ink-3">·</span>
            <span className="text-ink-3">2% risk</span>
            <span className="text-gold">${maxRisk.toFixed(2)}</span>
          </span>
        </div>

        {/* Total P&L */}
        {totalPnl !== 0 && (
          <div className="hidden md:block text-2xs font-mono">
            <span className="text-ink-3">Total </span>
            <span className={totalPnl >= 0 ? 'text-bull' : 'text-bear'}>
              {totalPnl >= 0 ? '+' : ''}${totalPnl.toFixed(2)}
            </span>
          </div>
        )}

        {/* WebSocket status */}
        <div className={clsx(
          'flex items-center gap-1 text-2xs font-mono',
          status === 'connected'  ? 'text-bull'  :
          status === 'connecting' ? 'text-amber'  : 'text-ink-3'
        )}>
          {status === 'connected'
            ? <Wifi size={12} />
            : status === 'connecting'
            ? <Activity size={12} className="animate-pulse" />
            : <WifiOff size={12} />
          }
          <span className="hidden sm:inline">
            {status === 'connected' ? 'Live' : status === 'connecting' ? 'Connecting' : 'Offline'}
          </span>
        </div>

        {/* Logout */}
        <button
          onClick={handleLogout}
          className="flex items-center gap-1.5 text-2xs font-mono text-ink-3 hover:text-bear transition-colors px-2 py-2 sm:py-1 rounded hover:bg-bear/10 border border-transparent hover:border-bear/20"
          title="Sign out"
        >
          <LogOut size={11} />
          <span className="hidden sm:inline">Sign out</span>
        </button>
      </div>
    </header>
  );
}
